<script setup lang="ts">
import { useElementSize, useResizeObserver } from "@vueuse/core";
import { Dropdown, Menu } from "floating-vue";
import { type Component, computed, getCurrentInstance, inject, nextTick, onMounted, provide, ref, watch } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";
import { type RouteLocationRaw, RouterLink } from "vue-router";
import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";

import DropdownMenuItem from "~/components/dumb/DropdownMenuItem.vue";
import LoadingSpinner from "~/components/dumb/LoadingSpinner.vue";
import MobileBottomSheet from "~/components/MobileBottomSheet.vue";
import { MAX_ITEMS_PER_SECTION, ROW_TYPE } from "~/constants/dropdown";
import { PARENT_ON_AFTER_CLOSE_FUNCTION, PARENT_ON_AFTER_OPEN_FUNCTION } from "~/constants/injection";
import { colorsByTheme } from "~/constants/style";
import { AlertIcon, RecommendWithAiIcon } from "~/icons";
import { isDefined } from "~/shared/common";
import { DropdownMenuItemKind, Placement, UserRole } from "~/shared/enums";
import type { DropdownMenuSection, ProcessedMenuItem } from "~/shared/types";
import { usePageStore, useUserStore } from "~/stores";

type ProcessedComponent = {
  title: string;
  items: ProcessedMenuItem[];
  showTitle?: boolean;
};

const props = defineProps<{
  container?: string;
  disabled?: boolean;
  sections: DropdownMenuSection[];
  placement?: Placement;
  block?: boolean;
  heightBlock?: boolean;
  distance?: number;
  skidding?: number;
  cover?: boolean;
  widthPixels?: number;
  maxHeightPixels?: number;
  dontPropagateClose?: boolean;
  isContrast?: boolean;
  showOnHover?: boolean;
  showRecommendationButton?: boolean;
  preventCloseOnSelect?: boolean;
  headerTitle?: string;
  showSearch?: boolean;
  hasInput?: boolean; // TODO this is a hack, remove it and solve a different way
  popperClass?: string;
  loading?: boolean;
  highlightOnly?: boolean;
  mobileIncludeBack?: boolean;
  mobileIncludeDone?: boolean;
  extraPadding?: boolean;
  grid?: boolean;
  gridColumns?: number;
  gridSpacing?: number;
  emoji?: boolean;
  enableShowMore?: boolean;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
  referenceNode?: Component | Element;
  onClick?: (event: Event) => void;
  onKeydown?: (event: KeyboardEvent) => void;
  onContextmenu?: (event: MouseEvent) => void;
}>();

const emit = defineEmits<{
  recommend: [];
}>();

const currentInstance = getCurrentInstance();
const pageStore = usePageStore();
const userStore = useUserStore();

const menu = ref<{ $el: HTMLDivElement } | null>(null);
const scrollContainer = computed<HTMLDivElement | null>(() => menu.value && menu.value.$el);
const colors = computed(() => colorsByTheme[pageStore.theme]);

const showMoreRefs = new Map();
const focusedShowMore = ref<string | null>(null);

const wrapOnClick = (fn?: (event?: Event) => void) => (event: Event) => {
  if (props.preventCloseOnSelect) {
    event.stopImmediatePropagation();
  }
  fn?.(event);
};

let currentItemIndex: number;
const indexOfFocusedItem = ref(props.highlightOnly ? 0 : -1);
const processedSections = computed(() => {
  const sections = [...props.sections];
  if (props.showRecommendationButton && pageStore.isOnline && userStore.isRoleGte(UserRole.MEMBER)) {
    sections.push({
      title: "Recommend",
      items: [
        {
          title: "Fill out with AI",
          kind: DropdownMenuItemKind.BUTTON,
          icon: RecommendWithAiIcon,
          iconArgs: { class: "icon-sm text-recommendation-base dark:text-recommendation-base" },
          onClick: () => emit("recommend"),
        },
      ],
    });
  }

  currentItemIndex = -1;
  return sections
    .map((sect) => {
      const unhiddenItems = sect.items.filter((e) => !e.hidden);
      if (unhiddenItems.length === 0) {
        return null;
      }
      const res: ProcessedComponent = {
        title: sect.title,
        showTitle: sect.showTitle,
        items: unhiddenItems.map((item) => {
          const { title, icon, subtitle, subtitleCopyDescription, selected, disabled, commandId, iconArgs, noFocus } =
            item;
          let component;
          let args;
          switch (item.kind) {
            case DropdownMenuItemKind.INTERNAL_LINK: {
              component = RouterLink;
              args = {
                onClick: wrapOnClick(item.onClick),
                to: item.navigate?.to as RouteLocationRaw,
                target: item.navigate?.newTab ? "_blank" : undefined,
              };
              break;
            }
            case DropdownMenuItemKind.EXTERNAL_LINK: {
              component = "a";
              args = {
                onClick: wrapOnClick(item.onClick),
                href: item.navigate?.to as string,
                target: item.navigate?.newTab ? "_blank" : undefined,
              };
              break;
            }
            case DropdownMenuItemKind.BUTTON: {
              component = "div";
              args = { onClick: wrapOnClick(item.onClick) };
              break;
            }
            case DropdownMenuItemKind.COMPONENT: {
              component = item.component as Component;
              args = item.componentArgs ?? {};
              break;
            }
            default: {
              throw new Error(`Unknown dropdown menu item kind: ${item.kind}`);
            }
          }
          const calcDisabled = disabled ?? false;
          if (!calcDisabled) {
            currentItemIndex += 1;
          }
          return {
            index: calcDisabled ? -1 : currentItemIndex,
            component,
            args,
            title,
            disabled: calcDisabled,
            disabledReason: item.disabledReason,
            selected: selected ?? false,
            noFocus,
            icon,
            iconArgs,
            subtitle,
            subtitleCopyDescription,
            strikethrough: item.strikethrough,
            commandId,
            dataTestid: item.dataTestid,
            onAssignRef: item.onAssignRef,
          };
        }),
      };
      return res;
    })
    .filter(isDefined);
});

const dropdownEmpty = computed(() => processedSections.value.length === 0 && !props.loading);

const expandedSections = ref<Set<string>>(new Set());
const mobileSheet = ref<ComponentExposed<typeof MobileBottomSheet<boolean>> | null>(null);

const filteredSections = computed(() => {
  const searchQuery = mobileSheet.value?.search?.trim()?.toLowerCase();
  if (!searchQuery) {
    return processedSections.value;
  }
  return processedSections.value
    .map((section) => ({
      ...section,
      items: section.items.filter(({ title, subtitle }) =>
        [title, subtitle].some((text) => text?.toLowerCase().includes(searchQuery))
      ),
    }))
    .filter((section) => section.items.length);
});

const hasSingleFilteredSection = computed(() => filteredSections.value.length === 1);

const rows = computed(() => {
  const res: {
    id: string;
    type: string;
    section: ProcessedComponent;
    item?: ProcessedComponent["items"][number];
  }[] = [];
  filteredSections.value.forEach((section, index) => {
    const isExpanded = expandedSections.value.has(section.title);
    const hasMoreItems = section.items.length > MAX_ITEMS_PER_SECTION + 1;
    const shouldLimitItems = props.enableShowMore && !hasSingleFilteredSection.value;
    const visibleItems =
      shouldLimitItems && hasMoreItems && !isExpanded ? section.items.slice(0, MAX_ITEMS_PER_SECTION) : section.items;

    if (!props.grid) {
      res.push({ id: `section-${index}-start`, type: ROW_TYPE.SECTION_START, section });
    }
    visibleItems.forEach((item, itemIndex) => {
      res.push({ id: `section-${index}-${itemIndex}`, type: ROW_TYPE.ITEM, section, item });
    });
    if (!props.grid && shouldLimitItems && section.showTitle && hasMoreItems && !isExpanded) {
      res.push({ id: `section-${index}-show-more`, type: ROW_TYPE.SHOW_MORE, section });
    }
    if (!props.grid) {
      res.push({ id: `section-${index}-end`, type: ROW_TYPE.SECTION_END, section });
    }
  });
  return res;
});

const placementDefinite = computed(() => props.placement ?? Placement.BOTTOM_LEFT);
const defaultDistance = computed(() => props.distance ?? (props.cover ? 0 : -8));
const calcDistance = ref(defaultDistance.value);
const calcWidthPx = computed(() => props.widthPixels ?? 224);
const maxHeightPixelsNorm = computed(() => props.maxHeightPixels ?? 384);
const showOnHoverNorm = computed(() => !pageStore.isMobile && props.showOnHover);

const menuSize = useElementSize(computed(() => menu.value?.$el));
const gridColumnsNorm = computed(() => props.gridColumns ?? 2);
const gridSpacingNorm = computed(() => props.gridSpacing ?? 6);
const gridItemSize = computed(() =>
  Math.round((menuSize.width.value === 0 ? calcWidthPx.value : menuSize.width.value) / gridColumnsNorm.value)
);

const itemRefs = new Map();

const assignItemRef = (
  index: number,
  elem: { $el: HTMLDivElement } | null,
  item: { onAssignRef?: (e: HTMLElement) => void }
) => {
  if (!elem || !elem.$el) {
    return;
  }
  itemRefs.set(index, elem.$el);
  item.onAssignRef?.(elem.$el);
};

const dropdownRef = ref<InstanceType<typeof Dropdown> | null>(null);
const wrapperRef = ref<HTMLDivElement | null>(null);
const recalculateDistance = () => {
  calcDistance.value = defaultDistance.value;
  if (!wrapperRef.value || !props.cover) {
    return;
  }
  calcDistance.value -= wrapperRef.value.getBoundingClientRect().height;
};
const assignWrapperRef = (elem: HTMLDivElement | null) => {
  wrapperRef.value = elem;
  recalculateDistance();
};
useResizeObserver(wrapperRef, recalculateDistance);
watch(() => props.cover, recalculateDistance);
watch(defaultDistance, recalculateDistance);

const focusDiv = ref<HTMLDivElement | null>(null);

const updateFocus = () => {
  if (indexOfFocusedItem.value === -1) {
    if (props.highlightOnly) {
      focusDiv.value?.scrollIntoView({ block: "nearest" });
    } else {
      focusDiv.value?.focus();
    }
    return;
  }
  if (props.highlightOnly) {
    itemRefs.get(indexOfFocusedItem.value)?.scrollIntoView({ block: "nearest" });
  } else {
    itemRefs.get(indexOfFocusedItem.value)?.focus();
  }
};

const toggleSection = (title: string) => {
  if (expandedSections.value.has(title)) {
    expandedSections.value.delete(title);
  } else {
    expandedSections.value.add(title);
    nextTick(() => {
      const section = processedSections.value.find((e) => e.title === title);
      if (section && section.items.length > MAX_ITEMS_PER_SECTION) {
        const firstNewItem = section.items[MAX_ITEMS_PER_SECTION];
        if (firstNewItem && !firstNewItem.disabled) {
          indexOfFocusedItem.value = firstNewItem.index;
          focusedShowMore.value = null;
          updateFocus();
        }
      }
    });
  }
};

const focusShowMore = (sectionTitle: string) => {
  indexOfFocusedItem.value = -1;
  focusedShowMore.value = sectionTitle;
};

const focusItem = (itemIndex: number) => {
  indexOfFocusedItem.value = itemIndex;
  focusedShowMore.value = null;
};

const moveUpOrDown = (isUp: boolean): boolean => {
  const items = rows.value.filter(
    (e) => (e.type === ROW_TYPE.ITEM && !e.item?.disabled) || e.type === ROW_TYPE.SHOW_MORE
  );
  if (!items.length) {
    return false;
  }
  const current = items.findIndex(
    (e) =>
      (e.type === ROW_TYPE.ITEM && e.item?.index === indexOfFocusedItem.value) ||
      (e.type === ROW_TYPE.SHOW_MORE && e.section.title === focusedShowMore.value)
  );

  const next = Math.max(0, Math.min(items.length - 1, current + (isUp ? -1 : 1)));
  if (next === current && current !== -1) {
    return false;
  }
  const row = items[next];
  indexOfFocusedItem.value = row.type === ROW_TYPE.ITEM && row.item ? row.item.index : -1;
  focusedShowMore.value = row.type === ROW_TYPE.SHOW_MORE ? row.section.title : null;
  nextTick(() => {
    if (focusedShowMore.value) {
      const el = showMoreRefs.get(focusedShowMore.value);
      if (el) {
        if (props.highlightOnly) {
          el.scrollIntoView({ block: "nearest" });
        } else {
          el.focus();
        }
      }
    } else {
      updateFocus();
    }
  });

  return true;
};

const isOpen = ref(false);

const isMobile = computed(() => pageStore.isMobile);

const onKeydown = (event: KeyboardEvent) => {
  if (!isOpen.value) {
    return;
  }
  const { key } = event;
  switch (key) {
    case "Enter":
    case "Shift":
    case "Meta":
    case "Control":
    case "Option":
    case "Alt": {
      break;
    }
    case "ArrowUp":
    case "ArrowDown": {
      const success = moveUpOrDown(key === "ArrowUp");
      if (success) {
        event.preventDefault();
      }
      break;
    }
    case "Tab": {
      event.preventDefault();
      moveUpOrDown(event.shiftKey);
      break;
    }
    default: {
      if (!props.hasInput) {
        dropdownRef.value?.hide();
      }
      break;
    }
  }
};

const onKeydownItem = (event: KeyboardEvent) => {
  if (!event.target || !(event.target instanceof HTMLElement)) {
    return;
  }
  const child = event.target.querySelector("div");
  if (!child) {
    return;
  }
  child.click();
};

const onShow = () => {
  isOpen.value = true;
  indexOfFocusedItem.value = props.highlightOnly ? 0 : -1;
  expandedSections.value.clear();
  focusedShowMore.value = null;
  props.onAfterOpen?.();
  nextTick(() => {
    updateFocus();
  });
};

const onHide = () => {
  isOpen.value = false;
  expandedSections.value.clear();
  focusedShowMore.value = null;
  props.onAfterClose?.();
};

const onMouseDown = (event: MouseEvent) => {
  if (!showOnHoverNorm.value || props.hasInput) {
    return;
  }
  event.preventDefault();
};

const isHidden = ref(false);
const parentOnAfterOpen = inject(PARENT_ON_AFTER_OPEN_FUNCTION);
const parentOnAfterClose = inject(PARENT_ON_AFTER_CLOSE_FUNCTION);

provide(PARENT_ON_AFTER_OPEN_FUNCTION, () => {
  if (!isMobile.value) {
    return;
  }
  isHidden.value = true;
});
provide(PARENT_ON_AFTER_CLOSE_FUNCTION, () => {
  if (!isMobile.value) {
    return;
  }
  isHidden.value = false;
});

const open = () => {
  if (isMobile.value && !props.disabled) {
    isOpen.value = true;
    expandedSections.value.clear();
    parentOnAfterOpen?.();
    props.onAfterOpen?.();
  } else {
    dropdownRef.value?.show();
  }
};

const close = () => {
  if (isMobile.value) {
    isOpen.value = false;
    expandedSections.value.clear();
    parentOnAfterClose?.();
    props.onAfterClose?.();
  } else {
    dropdownRef.value?.hide();
  }
};

const onMenuItemClick = (item?: ProcessedMenuItem) => {
  if (props.preventCloseOnSelect || (item?.component && typeof item.component === "object")) {
    return;
  }
  close();
};

const clickHighlightedItem = () => {
  if (focusedShowMore.value) {
    toggleSection(focusedShowMore.value);
    return;
  }

  const item = itemRefs.get(indexOfFocusedItem.value);
  if (!item) {
    return;
  }
  const child = item.querySelector("div");
  if (!child) {
    return;
  }
  child.click();
};

const setIndexOfFocusedItem = (index: number) => {
  indexOfFocusedItem.value = index;
};

onMounted(() => {
  if (!currentInstance) {
    return;
  }
  const instanceElem = currentInstance.subTree.el;
  if (!instanceElem?.children) {
    return;
  }

  assignWrapperRef(instanceElem.children[0] as HTMLDivElement);
});

defineExpose({
  open,
  close,
  isOpen,
  moveUpOrDown,
  clickHighlightedItem,
  setIndexOfFocusedItem,
  scrollContainer,
});
</script>

<template>
  <Dropdown
    v-if="!dropdownEmpty && !isMobile"
    ref="dropdownRef"
    :container="container"
    :reference-node="() => referenceNode"
    :disabled="disabled"
    :triggers="showOnHoverNorm ? ['hover', 'focus'] : undefined"
    :popper-triggers="showOnHoverNorm ? ['hover', 'focus'] : undefined"
    :placement="placementDefinite"
    :distance="calcDistance"
    :skidding="skidding"
    :dont-propagate-close="dontPropagateClose"
    no-auto-focus
    :theme="`dropdown-${pageStore.theme}`"
    class="flex"
    :class="{
      'max-w-full grow': block,
      'h-full': heightBlock,
      'cursor-pointer': !disabled,
    }"
    :popper-class="popperClass"
    @click="onClick"
    @keydown="onKeydown"
    @contextmenu="onContextmenu"
    @apply-show="onShow"
    @apply-hide="onHide">
    <template v-if="!referenceNode" #default>
      <slot />
    </template>
    <template #popper>
      <Menu no-auto-focus @mousedown="onMouseDown">
        <DynamicScroller
          ref="menu"
          :items="rows"
          :min-item-size="4"
          :item-size="grid ? gridItemSize : undefined"
          :grid-items="grid ? gridColumnsNorm : undefined"
          key-field="id"
          class="overflow-y-auto rounded border app-drag-none border-md focus-ring-none"
          :class="[block && 'w-full min-w-[224px]', isContrast ? 'bg-std' : 'bg-lt', emoji && 'pl-2 pt-2']"
          :style="{
            'max-height': `calc(min(${maxHeightPixelsNorm}px,100vh))`,
            width: `${calcWidthPx}px`,
            'max-width': `${calcWidthPx}px`,
            '--background': isContrast ? colors.bgStd : colors.bgLt,
            '--highlight': isContrast ? colors.borderLt : colors.borderMd,
          }"
          @click.stop="updateFocus"
          @keydown.stop="onKeydown">
          <template v-if="!hasInput" #before>
            <div ref="focusDiv" tabindex="0" class="focus:outline-none" />
          </template>
          <template #default="{ item: { id, section, item, type }, index, active }">
            <!-- Section start and end -->
            <DynamicScrollerItem
              v-if="(type === ROW_TYPE.SECTION_START || type === ROW_TYPE.SECTION_END) && !grid"
              :item="{ id }"
              :active="active"
              :size-dependencies="[]"
              class="w-full truncate text-vlt border-md"
              :class="{
                'border-b pb-1': type === ROW_TYPE.SECTION_END && index < rows.length - 1,
                'pt-1': type === ROW_TYPE.SECTION_START,
              }">
              <span
                v-if="section.showTitle && type === ROW_TYPE.SECTION_START"
                class="select-none px-3 text-xs/3 font-semibold uppercase">
                {{ section.title }}
              </span>
            </DynamicScrollerItem>
            <!-- Show more -->
            <DynamicScrollerItem
              v-else-if="type === ROW_TYPE.SHOW_MORE && !grid && enableShowMore && section.showTitle"
              :item="{ id }"
              :active="active"
              :tabindex="0"
              class="w-full focus-ring-none"
              :class="[!highlightOnly && 'focus:bg-md', highlightOnly && focusedShowMore === section.title && 'bg-md']"
              @keydown.enter.stop="toggleSection(section.title)"
              @mouseover="focusShowMore(section.title)"
              @focusin="focusShowMore(section.title)">
              <button
                type="button"
                class="flex w-full items-center justify-center py-1.5 text-xs font-medium text-vlt"
                tabindex="-1"
                @click.prevent.stop="toggleSection(section.title)"
                @mousedown.prevent>
                Show more
              </button>
            </DynamicScrollerItem>
            <!-- Item -->
            <DynamicScrollerItem
              v-else-if="item"
              :ref="(elem: never) => assignItemRef(item.index, elem, item)"
              :item="{ id }"
              :active="active"
              :size-dependencies="[]"
              :tabindex="item.disabled || item.noFocus ? undefined : 0"
              :disabled="item.disabled"
              class="focus-ring-none"
              :class="[
                item.disabled && 'cursor-default',
                !highlightOnly && 'focus:bg-md focus-visible:bg-md',
                highlightOnly && indexOfFocusedItem === item.index && 'bg-md',
                grid && 'flex h-full items-center justify-center rounded',
              ]"
              :style="{
                width: grid ? `${gridItemSize - gridSpacingNorm}px` : undefined,
                height: grid ? `${gridItemSize - gridSpacingNorm}px` : undefined,
              }"
              @keydown.enter.stop="onKeydownItem"
              @mouseover="focusItem(item.index)"
              @focusin="focusItem(item.index)">
              <DropdownMenuItem
                :item="item"
                :highlight-only="highlightOnly"
                :grid="grid"
                :emoji="emoji"
                @click="close" />
            </DynamicScrollerItem>
          </template>
          <template v-if="loading" #after>
            <LoadingSpinner class="py-1.5" small />
          </template>
        </DynamicScroller>
        <Teleport v-if="isOpen" to="body">
          <div class="absolute inset-0" @click="close" @keydown.escape="close" />
        </Teleport>
      </Menu>
    </template>
  </Dropdown>

  <template v-else-if="!dropdownEmpty && isMobile">
    <div
      v-bind="$attrs"
      class="flex"
      :class="[
        $attrs.class,
        {
          'max-w-full grow': block,
          'h-full': heightBlock,
          'cursor-pointer': !disabled,
        },
      ]"
      @click="open"
      @keydown="open">
      <slot />
    </div>

    <MobileBottomSheet
      ref="mobileSheet"
      :entity="isOpen"
      :include-back="mobileIncludeBack"
      :hidden="isHidden"
      :title="headerTitle"
      :include-done="mobileIncludeDone"
      :include-search="showSearch"
      :should-update-keyboard-height="showSearch"
      @close="close">
      <div class="size-full divide-y overflow-y-auto divide-md">
        <div
          v-for="(section, sectionIndex) in filteredSections"
          :key="`section-${sectionIndex}`"
          class="flex flex-col py-1"
          :class="[extraPadding ? 'pl-5 pr-4' : 'px-2', showSearch && 'max-h-80']">
          <span v-if="section.showTitle" class="mt-3 select-none px-3 text-xs font-semibold uppercase text-vlt">
            {{ section.title }}
          </span>
          <template v-for="(item, itemIndex) in section.items" :key="`section-${sectionIndex}-${itemIndex}`">
            <DropdownMenuItem
              v-if="
                !enableShowMore ||
                hasSingleFilteredSection ||
                itemIndex < MAX_ITEMS_PER_SECTION ||
                expandedSections.has(section.title)
              "
              :item="item"
              :highlight-only="highlightOnly"
              :grid="grid"
              @click="() => onMenuItemClick(item)" />
          </template>
          <button
            v-if="
              enableShowMore &&
              !hasSingleFilteredSection &&
              section.showTitle &&
              section.items.length > MAX_ITEMS_PER_SECTION + 1 &&
              !expandedSections.has(section.title)
            "
            type="button"
            class="mt-1 flex w-full items-center justify-center rounded py-1.5 text-xs font-medium text-vlt"
            @click.prevent.stop="toggleSection(section.title)"
            @mousedown.prevent>
            Show more
          </button>
        </div>
        <div
          v-if="filteredSections.length === 0"
          tabindex="0"
          class="flex cursor-default select-none items-center gap-2 py-3.5 pl-5 pr-2 opacity-50 text-vlt">
          <AlertIcon class="text-vlt icon-md" />
          <span>No results, search again</span>
        </div>
      </div>
    </MobileBottomSheet>
  </template>
</template>

<style>
.v-popper--theme-dropdown .v-popper__arrow-container {
  display: none;
}
.v-popper--theme-dropdown .v-popper__wrapper {
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.v-popper--theme-dropdown .v-popper__inner {
  border-width: 0px;
  border-radius: 4px;
  background-color: transparent;
}
</style>
